import { useCallback, useEffect, useState } from "react";
import { fetchCompanyInvoice } from "../api/companyApi";
import { fetchHospitalInvoice } from "../api/hospitalApi";
import { fetchAdminInvoice } from "../api/adminApi";
import {
  approveAuditorInvoice,
  fetchAuditorInvoice,
  rejectAuditorInvoice,
  startAuditorInvoiceReview,
} from "../api/auditorApi";
import { ApiError, sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { getEnterpriseInvoiceDetail } from "../data";
import type { CompanyInvoiceDetail } from "../api/types";
import type { EnterpriseInvoiceDetail } from "../types";
import { getAuthToken } from "../auth";

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.includes("/") ? value : "—";
  }
  return date.toLocaleDateString("fr-FR");
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString("fr-FR")} à ${date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function mapApiDetail(row: CompanyInvoiceDetail): EnterpriseInvoiceDetail {
  const displayStatus =
    row.display_status === "approved"
      ? "approved"
      : row.display_status === "rejected"
        ? "rejected"
        : row.display_status === "submitted"
          ? "submitted"
          : "inReview";

  return {
    displayId: row.id,
    patientEmployee: row.patient_employee,
    company: resolveCompanyName(row),
    hospital: row.hospital,
    treatmentType: row.treatment_type,
    invoiceNature: row.invoice_nature,
    isForfaitMensuel: row.is_forfait_mensuel,
    providerReference: row.provider_reference,
    periodStart: formatDate(row.period_start),
    periodEnd: formatDate(row.period_end),
    submissionNotes: row.submission_notes || "—",
    medicalActs: row.medical_acts.map((act) => ({
      id: String(act.id),
      code: act.code || "—",
      label: act.name || "—",
      cost: act.total_price || act.unit_price,
      date: act.date_of_act
        ? new Date(act.date_of_act).toLocaleDateString("fr-FR")
        : "—",
    })),
    totalAmount: row.total_amount,
    coveredAmount: row.covered_amount ?? 0,
    submittedAt: formatDateTime(row.submitted_at),
    status: displayStatus,
    fileName: row.file_name,
    legacyStatus: row.status,
    canEdit: row.can_edit === true,
    hospitalId: row.hospital_id ?? null,
    invoiceCurrency: row.invoice_currency === "CDF" ? "CDF" : "USD",
    exchangeRateCdf: row.exchange_rate_cdf ?? null,
    hospitalRateToday: row.hospital_rate_today ?? null,
    hospitalUsesCdf: row.hospital_uses_cdf === true,
    hasAiAnalysis: row.has_ai_analysis === true,
    forfaitHasClientRate: row.forfait_has_client_rate === true,
    companyBillingStatementId: row.company_billing_statement_id ?? null,
    montantApprouve: row.montant_approuve ?? null,
    validatedByName: row.validated_by_name ?? null,
    validatedAt: row.validated_at ?? null,
    auditNotes: row.audit_notes ?? "",
    rejectionReason: row.rejection_reason ?? "",
    reviewsValidation: row.reviews_validation,
    retainedTotal: row.retained_total,
    attachments: (row.attachments ?? []).map((att) => ({
      id: att.id,
      original_name: att.original_name,
      mime_type: att.mime_type,
      file_size: att.file_size,
      created_at: att.created_at,
    })),
  };
}

function resolveCompanyName(row: CompanyInvoiceDetail): string {
  const company = (row.company ?? "").trim();
  const notes = (row.submission_notes ?? "").trim();
  if (!company || (notes !== "" && company === notes)) {
    return "—";
  }
  return company;
}

export function useEnterpriseInvoiceDetail(
  displayId: number,
  scope: "company" | "hospital" | "auditor" | "admin" = "company",
) {
  const [invoice, setInvoice] = useState<EnterpriseInvoiceDetail | null>(() =>
    displayId > 0 ? getEnterpriseInvoiceDetail(displayId) : null,
  );
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!displayId || displayId <= 0) {
      setInvoice(null);
      return;
    }

    if (!getAuthToken()) {
      setUsingApi(false);
      setInvoice(getEnterpriseInvoiceDetail(displayId));
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const row =
        scope === "hospital"
          ? await fetchHospitalInvoice(displayId)
          : scope === "auditor"
            ? await fetchAuditorInvoice(displayId)
            : scope === "admin"
              ? await fetchAdminInvoice(displayId)
              : await fetchCompanyInvoice(displayId);
      setInvoice(mapApiDetail(row));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(null);
        setInvoice(null);
        return;
      }
      const message = sanitizeApiErrorMessage(
        err instanceof Error ? err.message : "Facture introuvable.",
      );
      setError(message);
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [displayId, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const startReview = useCallback(async () => {
    if (scope !== "auditor" || !getAuthToken()) return false;
    setActing(true);
    setError(null);
    try {
      const row = await startAuditorInvoiceReview(displayId);
      setInvoice(mapApiDetail(row));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de démarrer l'examen.");
      return false;
    } finally {
      setActing(false);
    }
  }, [displayId, scope]);

  const applyExchangeRate = useCallback(
    async (rateCdfPerUsd?: number | null) => {
      if ((scope !== "auditor" && scope !== "admin") || !getAuthToken()) return false;
      setActing(true);
      setError(null);
      try {
        const { applyAuditorInvoiceExchangeRate } = await import("../api/auditorApi");
        const { applyAdminInvoiceExchangeRate } = await import("../api/adminApi");
        const row =
          scope === "admin"
            ? await applyAdminInvoiceExchangeRate(displayId, rateCdfPerUsd)
            : await applyAuditorInvoiceExchangeRate(displayId, rateCdfPerUsd);
        setInvoice(mapApiDetail(row));
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Impossible d'appliquer le taux.");
        return false;
      } finally {
        setActing(false);
      }
    },
    [displayId, scope],
  );

  const approve = useCallback(
    async (payload: { audit_notes?: string; montant_approuve?: number } = {}) => {
      if (scope !== "auditor" || !getAuthToken()) return false;
      setActing(true);
      setError(null);
      try {
        const row = await approveAuditorInvoice(displayId, payload);
        setInvoice(mapApiDetail(row));
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Approbation impossible.");
        return false;
      } finally {
        setActing(false);
      }
    },
    [displayId, scope],
  );

  const reject = useCallback(
    async (payload: { rejection_reason: string; audit_notes?: string }) => {
      if (scope !== "auditor" || !getAuthToken()) return false;
      setActing(true);
      setError(null);
      try {
        const row = await rejectAuditorInvoice(displayId, payload);
        setInvoice(mapApiDetail(row));
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Rejet impossible.");
        return false;
      } finally {
        setActing(false);
      }
    },
    [displayId, scope],
  );

  return {
    invoice,
    loading,
    acting,
    error,
    usingApi,
    reload: load,
    startReview,
    applyExchangeRate,
    approve,
    reject,
  };
}
